import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ClipboardCheck, Target } from "lucide-react";
import { DeleteRequestAlert, DeleteResultAlert } from "@/components/DeleteRequestAlerts";
import { OpeningIssueAlert, OpeningActionAlert, OpeningOverdueAlert } from "@/components/opening/OpeningAlerts";
import { fmtNum } from "@/lib/helpers";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Istanbul" }) : "-");

export function SaleDeletedAlert() {
  const [items, setItems] = useState([]);
  const tick = useRealtimeTick((m) => m.path.startsWith("/api/sales") || m.path.startsWith("/api/notifications") || m.path.startsWith("/api/day-end") || m.path.startsWith("/api/opening"));
  useEffect(() => { http.get("/notifications/alerts").then((r) => setItems(r.data)).catch(() => {}); }, [tick]);
  const cur = items[0];
  const ack = async () => {
    await http.post(`/notifications/${cur.id}/ack`).catch(() => {});
    setItems((x) => x.slice(1));
  };
  if (!cur) return null;
  const m = cur.meta || {};
  if (cur.kind === "target_reminder") return <TargetReminderAlert m={m} rest={items.length - 1} ack={ack} />;
  if (cur.kind === "delete_request") return <DeleteRequestAlert m={m} rest={items.length - 1} ack={ack} />;
  if (cur.kind === "delete_result") return <DeleteResultAlert m={m} rest={items.length - 1} ack={ack} />;
  if (cur.kind === "opening_issue") return <OpeningIssueAlert m={m} rest={items.length - 1} ack={ack} />;
  if (cur.kind === "opening_action") return <OpeningActionAlert m={m} rest={items.length - 1} ack={ack} />;
  if (cur.kind === "opening_overdue") return <OpeningOverdueAlert m={m} rest={items.length - 1} ack={ack} />;
  if (cur.kind === "day_end") return <DayEndAlert cur={cur} m={m} rest={items.length - 1} ack={ack} />;
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent overlayClassName="z-[100]" className="z-[100] bg-slate-900 border-2 border-red-500/60 max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()}
                     onEscapeKeyDown={(e) => e.preventDefault()} data-testid="sale-deleted-alert">
        <DialogHeader>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-red-500/15 border border-red-500/40 flex items-center justify-center mb-2">
            <AlertTriangle size={32} className="text-red-400" />
          </div>
          <DialogTitle className="text-center font-display text-2xl text-red-300">Satışınız Silindi</DialogTitle>
        </DialogHeader>
        <div className="rounded-xl bg-slate-950/60 border border-slate-800 divide-y divide-slate-800 text-sm">
          {[["Ürün", `${m.product_name || "-"} ×${m.quantity ?? "-"}`], ["Müşteri", m.customer_name || "-"], ["Satış Tarihi", fmt(m.sale_created_at)],
            ["Silen", m.deleted_by || "-"], ["Silinme Zamanı", fmt(m.deleted_at || cur.created_at)]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 px-4 py-2"><span className="text-slate-500">{k}</span><span className="font-semibold text-right">{v}</span></div>
          ))}
        </div>
        {items.length > 1 && <p className="text-center text-xs text-slate-500">{items.length - 1} silme bildirimi daha var</p>}
        <div><Button onClick={ack} className="w-full py-6 bg-red-600 hover:bg-red-500 text-base" data-testid="sale-deleted-ok">Tamam</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function DayEndAlert({ cur, m, rest, ack }) {
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent overlayClassName="z-[100]" className="z-[100] bg-slate-900 border-2 border-emerald-500/60 max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()}
                     onEscapeKeyDown={(e) => e.preventDefault()} data-testid="day-end-alert">
        <DialogHeader>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-2">
            <ClipboardCheck size={32} className="text-emerald-400" />
          </div>
          <DialogTitle className="text-center font-display text-2xl text-emerald-300">Gün Sonu Özeti</DialogTitle>
          <p className="text-center text-sm text-slate-400">{m.branch_name} · {m.date}</p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-3 text-center"><div className="text-[10px] uppercase font-mono text-slate-500">Toplam Adet</div>
            <div className="font-display text-3xl font-bold text-emerald-300" data-testid="day-end-alert-total">{fmtNum(m.total_qty)}</div></div>
          <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-3 text-center"><div className="text-[10px] uppercase font-mono text-slate-500">İşlem</div>
            <div className="font-display text-3xl font-bold">{fmtNum(m.sale_count)}</div></div>
        </div>
        <div className="rounded-xl bg-slate-950/60 border border-slate-800 divide-y divide-slate-800 text-sm max-h-56 overflow-y-auto">
          {(m.groups || []).map((g) => (
            <div key={g.name} className="flex justify-between gap-3 px-4 py-2"><span className="text-slate-300">{g.name}</span><b className="tabular-nums">{fmtNum(g.qty)}</b></div>
          ))}
          {!(m.groups || []).length && <div className="px-4 py-4 text-center text-slate-500">Bugün işlem yok</div>}
        </div>
        {rest > 0 && <p className="text-center text-xs text-slate-500">{rest} bildirim daha var</p>}
        <div><Button onClick={ack} className="w-full py-6 bg-emerald-600 hover:bg-emerald-500 text-base" data-testid="day-end-alert-ok">Tamam</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function TargetReminderAlert({ m, rest, ack }) {
  const stats = [["Olması Gereken", m.expected, "text-slate-200"], ["Satılan", m.total_sold, "text-blue-300"], ["Kalan", m.remaining, "text-amber-300"]];
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent overlayClassName="z-[100]" className="z-[100] bg-slate-900 border-2 border-amber-500/60 max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()}
                     onEscapeKeyDown={(e) => e.preventDefault()} data-testid="target-reminder-alert">
        <DialogHeader>
          <div className="mx-auto h-16 w-16 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-2">
            <Target size={32} className="text-amber-400" />
          </div>
          <DialogTitle className="text-center font-display text-2xl text-amber-300">Hedef Hatırlatma</DialogTitle>
          <p className="text-center text-sm text-slate-400">Aylık hedefinin gerisindesin · ayın bitmesine {m.days_left} gün</p>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {stats.map(([l, v, c]) => (
            <div key={l} className="rounded-xl bg-slate-950/60 border border-slate-800 p-2.5 text-center"><div className="text-[10px] uppercase font-mono text-slate-500">{l}</div>
              <div className={`font-display text-2xl font-bold ${c}`}>{fmtNum(v)}</div></div>
          ))}
        </div>
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/40 p-4 text-center">
          <div className="text-xs uppercase font-mono text-amber-200">Bugün gereken</div>
          <div className="font-display text-5xl font-black text-amber-300" data-testid="target-reminder-today">{fmtNum(m.today_needed)} <span className="text-lg">adet</span></div>
        </div>
        {(m.lines || []).length > 0 && (
          <div className="rounded-xl bg-slate-950/60 border border-slate-800 divide-y divide-slate-800 text-sm max-h-44 overflow-y-auto">
            {m.lines.map((l) => (
              <div key={l.name} className="flex justify-between gap-3 px-4 py-2"><span className="text-slate-300 truncate">{l.name}</span>
                <span className="tabular-nums shrink-0">kalan <b>{fmtNum(l.remaining)}</b> · bugün <b className="text-amber-300">{fmtNum(l.today_needed)}</b></span></div>
            ))}
          </div>
        )}
        {rest > 0 && <p className="text-center text-xs text-slate-500">{rest} bildirim daha var</p>}
        <div><Button onClick={ack} className="w-full py-6 bg-amber-600 hover:bg-amber-500 text-base" data-testid="target-reminder-ok">Anladım, Satışa!</Button></div>
      </DialogContent>
    </Dialog>
  );
}
